import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Download, Search, Filter, CheckCircle, Clock, Shield, Trash2, ChevronRight, ChevronDown, ChevronUp, Loader2, AlertTriangle, RefreshCw, Calendar } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { format, differenceInDays, isPast } from "date-fns";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { CollapsibleRecordRow, CollapsibleRecordList } from "@/components/governance/CollapsibleRecordRow";
import { useMobile } from "@/hooks/useMobile";
import { AttestationRenewal, AttestationExpirationBadge } from "@/components/governance/AttestationRenewal";
import { getDocumentSignedUrl } from "@/lib/storageUtils";

interface GovernanceDocument {
  id: string;
  document_title: string;
  document_type: string;
  document_category: string;
  effective_date: string | null;
  expiration_date: string | null;
  created_at: string;
  all_signatures_collected: boolean;
  access_level: string;
  signed_file_url: string | null;
  content_html: string | null;
  tags: string[];
  finalized_at: string | null;
  version: number | null;
  is_current_version: boolean | null;
}

// Filter Card Component with Mobile Collapsible
function FilterCard({
  searchTerm,
  setSearchTerm,
  categoryFilter,
  setCategoryFilter,
  typeFilter,
  setTypeFilter,
  statusFilter,
  setStatusFilter,
}: {
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  categoryFilter: string;
  setCategoryFilter: (v: string) => void;
  typeFilter: string;
  setTypeFilter: (v: string) => void;
  statusFilter: string;
  setStatusFilter: (v: string) => void;
}) {
  const { isMobile } = useMobile();
  const [filtersOpen, setFiltersOpen] = useState(!isMobile);

  const hasActiveFilters = categoryFilter !== "all" || typeFilter !== "all" || statusFilter !== "all";

  return (
    <Card className="mb-6">
      <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
        <CardHeader className="pb-3">
          <CollapsibleTrigger asChild>
            <div className="flex items-center justify-between cursor-pointer">
              <CardTitle className="flex items-center gap-2">
                <Filter className="h-5 w-5" />
                Filter & Search
                {hasActiveFilters && (
                  <Badge variant="secondary" className="ml-2 text-xs">
                    Active
                  </Badge>
                )}
              </CardTitle>
              {isMobile && (
                filtersOpen ? (
                  <ChevronUp className="h-5 w-5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-muted-foreground" />
                )
              )}
            </div>
          </CollapsibleTrigger>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by title or tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 min-h-[44px]"
                />
              </div>

              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="min-h-[44px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="membership">Membership</SelectItem>
                  <SelectItem value="governance">Governance</SelectItem>
                  <SelectItem value="financial">Financial</SelectItem>
                  <SelectItem value="property">Property</SelectItem>
                  <SelectItem value="legal">Legal</SelectItem>
                </SelectContent>
              </Select>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="min-h-[44px]">
                  <SelectValue placeholder="Document Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="member_appointment_resolution">Member Appointment</SelectItem>
                  <SelectItem value="bylaws">Bylaws</SelectItem>
                  <SelectItem value="articles_of_association">Articles of Association</SelectItem>
                  <SelectItem value="operating_agreement">Operating Agreement</SelectItem>
                  <SelectItem value="financial_statement">Financial Statement</SelectItem>
                </SelectContent>
              </Select>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="min-h-[44px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="complete">Fully Signed</SelectItem>
                  <SelectItem value="pending">Pending Signatures</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}

export default function GovernanceDocuments() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [userRole, setUserRole] = useState<string | null>(null);
  const [deletingDocId, setDeletingDocId] = useState<string | null>(null);
  const [showAllDocuments, setShowAllDocuments] = useState(false);
  const [renewalDialogOpen, setRenewalDialogOpen] = useState(false);
  const [selectedAttestationId, setSelectedAttestationId] = useState<string | null>(null);

  const { data: documents, isLoading, refetch } = useQuery({
    queryKey: ["governance-documents"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // First check if user is a Secretary Treasurer
      const { data: unaAsTS } = await supabase
        .from("unas")
        .select("id")
        .eq("treasury_secretary_id", user.id)
        .maybeSingle();

      let unaId: string | null = null;
      let role: string | null = null;

      if (unaAsTS) {
        unaId = unaAsTS.id;
        role = 'treasury_secretary';
      } else {
        // Fall back to checking una_members table
        const { data: membership } = await supabase
          .from("una_members")
          .select("una_id, member_role")
          .eq("user_id", user.id)
          .eq("status", "active")
          .maybeSingle();

        if (membership) {
          unaId = membership.una_id;
          role = membership.member_role;
        }
      }

      if (!unaId) throw new Error("No active UNA membership");
      
      setUserRole(role);

      // Fetch governance documents (exclude Meeting Prep, Meetings, Resolutions, and Journal entries)
      const { data, error } = await supabase
        .from("governance_documents")
        .select("*")
        .eq("una_id", unaId)
        .eq("is_current_version", true)
        .not("document_category", "eq", "meeting_prep")
        .not("document_category", "eq", "Meeting Prep")
        .not("document_category", "eq", "meetings")
        .not("document_category", "eq", "Meetings")
        .not("document_category", "eq", "resolutions")
        .not("document_category", "eq", "Resolutions")
        .not("document_category", "eq", "journal")
        .not("document_category", "eq", "Journal")
        .not("document_category", "eq", "log")
        .not("document_category", "eq", "Log")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Set up real-time subscription for document changes
  useEffect(() => {
    const channel = supabase
      .channel('governance-documents-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'governance_documents',
        },
        (payload) => {
          console.log('Governance document change:', payload);
          if (payload.eventType === 'INSERT') {
            toast.success("New document archived!", {
              description: `${payload.new.document_title} has been added to the governance repository.`,
            });
          } else if (payload.eventType === 'DELETE') {
            toast.info("Document removed from repository");
          }
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetch]);

  // Filter documents
  const filteredDocuments = documents?.filter((doc) => {
    const matchesSearch = doc.document_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (doc.tags && doc.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())));
    const matchesCategory = categoryFilter === "all" || doc.document_category === categoryFilter;
    const matchesType = typeFilter === "all" || doc.document_type === typeFilter;
    const matchesStatus = statusFilter === "all" ||
      (statusFilter === "complete" && doc.all_signatures_collected) ||
      (statusFilter === "pending" && !doc.all_signatures_collected);

    return matchesSearch && matchesCategory && matchesType && matchesStatus;
  });

  const [isGeneratingPdf, setIsGeneratingPdf] = useState<string | null>(null);

  const handleDownload = async (doc: GovernanceDocument) => {
    // If we have a signed_file_url, just download it
    if (doc.signed_file_url) {
      try {
        const sanitizedTitle = doc.document_title.replace(/[^a-z0-9]/gi, '_').toLowerCase();
        const filename = `${sanitizedTitle}.pdf`;
        const signedUrl = await getDocumentSignedUrl({ signed_file_url: doc.signed_file_url });
        const downloadUrl = signedUrl ? `${signedUrl}${signedUrl.includes('?') ? '&' : '?'}download=${filename}` : null;

        if (!downloadUrl) throw new Error('Unable to create a download link');
        
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast.success('Document download started');
      } catch (error) {
        console.error('Download error:', error);
        toast.error('Failed to download document');
      }
      return;
    }

    // No signed_file_url - try to generate PDF from content_html
    setIsGeneratingPdf(doc.id);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Please log in to download');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ensure-governance-document-pdf`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ governanceDocumentId: doc.id }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to generate PDF');
      }

      const result = await response.json();
      if (result.signed_file_url) {
        // Refetch to get updated data
        refetch();
        
        // Open via signed URL
        const url = await getDocumentSignedUrl({ signed_file_url: result.signed_file_url });
        if (url) window.open(url, '_blank');
        toast.success('PDF generated and opened');
      }
    } catch (error: any) {
      console.error('PDF generation error:', error);
      toast.error(error.message || 'Failed to generate PDF');
    } finally {
      setIsGeneratingPdf(null);
    }
  };

  const handleDelete = async (documentId: string) => {
    setDeletingDocId(documentId);
    try {
      const { error } = await supabase
        .from('governance_documents')
        .delete()
        .eq('id', documentId);

      if (error) throw error;

      toast.success('Document deleted successfully');
      refetch();
    } catch (error: any) {
      console.error('Delete error:', error);
      toast.error('Failed to delete document', {
        description: error.message || 'You may not have permission to delete this document'
      });
    } finally {
      setDeletingDocId(null);
    }
  };

  const canDelete = userRole === 'treasury_secretary';

  const getCategoryBadge = (category: string) => {
    const colors: Record<string, string> = {
      membership: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
      governance: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
      financial: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
      property: "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200",
      legal: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
    };
    return colors[category?.toLowerCase()] || "bg-gray-100 text-gray-800";
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading governance documents...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  const documentsToShow = showAllDocuments ? filteredDocuments : filteredDocuments?.slice(0, 3);
  const hasMoreDocuments = (filteredDocuments?.length || 0) > 3;

  // Find attestations that need renewal (expiring within 30 days or expired)
  const attestationsNeedingRenewal = documents?.filter(doc => {
    if (doc.document_type !== 'attestation' || !doc.expiration_date || !doc.is_current_version) return false;
    const daysUntil = differenceInDays(new Date(doc.expiration_date), new Date());
    return daysUntil <= 30 || isPast(new Date(doc.expiration_date));
  }) || [];

  const handleRenewalClick = (docId: string) => {
    setSelectedAttestationId(docId);
    setRenewalDialogOpen(true);
  };

  return (
    <MainLayout>
      <div className="container mx-auto py-8 px-4 max-w-7xl">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">Governance Documents</h1>
          <p className="text-muted-foreground">
            Permanent repository for your UNA's important governance documents
          </p>
        </div>

        {/* Attestation Renewal Alert */}
        {attestationsNeedingRenewal.length > 0 && (
          <Card className="mb-6 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
                <AlertTriangle className="h-5 w-5" />
                Attestation Renewal Required
              </CardTitle>
              <CardDescription className="text-amber-700">
                The following attestations need to be renewed to maintain your ecclesiastical standing
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {attestationsNeedingRenewal.map(doc => {
                  const daysUntil = differenceInDays(new Date(doc.expiration_date!), new Date());
                  const isExpired = isPast(new Date(doc.expiration_date!));
                  
                  return (
                    <div 
                      key={doc.id}
                      className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-amber-600" />
                        <div>
                          <p className="font-medium">{doc.document_title}</p>
                          <p className="text-sm text-muted-foreground">
                            Version {doc.version || 1} • {isExpired 
                              ? `Expired ${Math.abs(daysUntil)} days ago`
                              : `Expires in ${daysUntil} days`}
                          </p>
                        </div>
                      </div>
                      <Button
                        variant={isExpired ? "destructive" : "default"}
                        size="sm"
                        onClick={() => handleRenewalClick(doc.id)}
                      >
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Renew Now
                      </Button>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Renewal Dialog */}
        <Dialog open={renewalDialogOpen} onOpenChange={setRenewalDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Renew Your Attestation</DialogTitle>
              <DialogDescription>
                Review and re-sign your commitment to maintain your ecclesiastical standing
              </DialogDescription>
            </DialogHeader>
            {selectedAttestationId && (
              <AttestationRenewal 
                governanceDocumentId={selectedAttestationId}
                onRenewalComplete={() => {
                  setRenewalDialogOpen(false);
                  setSelectedAttestationId(null);
                  refetch();
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Recently Archived Section - Simplified */}
        {documents && documents.length > 0 && (
          <Card className="mb-6 bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Recently Archived
              </CardTitle>
              <CardDescription>Latest documents added to governance repository</CardDescription>
            </CardHeader>
            <CardContent>
              <CollapsibleRecordList maxVisible={3}>
                {documents.slice(0, 5).map((doc) => (
                  <CollapsibleRecordRow
                    key={doc.id}
                    id={doc.id}
                    title={doc.document_title}
                    date={doc.created_at}
                    type="document"
                    badge={{
                      label: doc.document_category,
                      className: getCategoryBadge(doc.document_category),
                    }}
                    subtitle={doc.all_signatures_collected ? 'Fully Signed' : 'Pending Signatures'}
                    onClick={() => navigate(`/governance-documents/${doc.id}`)}
                    actions={
                      <div className="flex items-center gap-1">
                        {doc.all_signatures_collected && (
                          <Badge variant="default" className="bg-green-100 text-green-800 text-xs">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Signed
                          </Badge>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(doc);
                          }}
                          disabled={isGeneratingPdf === doc.id || (!doc.signed_file_url && !doc.content_html)}
                        >
                          {isGeneratingPdf === doc.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </Button>
                        {canDelete && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive hover:text-destructive"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Governance Document?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to permanently delete "{doc.document_title}"? 
                                  This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(doc.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  {deletingDocId === doc.id ? "Deleting..." : "Delete"}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    }
                    expandedContent={
                      <div className="space-y-2 text-sm">
                        <p>
                          <span className="text-muted-foreground">Category:</span>{' '}
                          {doc.document_category}
                        </p>
                        {doc.effective_date && (
                          <p>
                            <span className="text-muted-foreground">Effective:</span>{' '}
                            {format(new Date(doc.effective_date), 'MMMM d, yyyy')}
                          </p>
                        )}
                        <p>
                          <span className="text-muted-foreground">Added:</span>{' '}
                          {format(new Date(doc.created_at), "MMM d, yyyy 'at' h:mm a")}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/governance-documents/${doc.id}`);
                          }}
                        >
                          View Details
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    }
                  />
                ))}
              </CollapsibleRecordList>
            </CardContent>
          </Card>
        )}

        {/* Filter Card - Collapsible on Mobile */}
        <FilterCard
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          categoryFilter={categoryFilter}
          setCategoryFilter={setCategoryFilter}
          typeFilter={typeFilter}
          setTypeFilter={setTypeFilter}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
        />

        {/* All Documents - Simplified with Collapsible Rows */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              All Documents
              {filteredDocuments && filteredDocuments.length > 0 && (
                <Badge variant="secondary">{filteredDocuments.length}</Badge>
              )}
            </CardTitle>
            <CardDescription>
              View and download your UNA's governance documents
            </CardDescription>
          </CardHeader>
          <CardContent>
            {filteredDocuments && filteredDocuments.length > 0 ? (
              <div className="space-y-2">
                {documentsToShow?.map((doc) => (
                  <CollapsibleRecordRow
                    key={doc.id}
                    id={doc.id}
                    title={doc.document_title}
                    date={doc.created_at}
                    type="document"
                    badge={{
                      label: doc.requires_signatures
                        ? (doc.all_signatures_collected ? 'Complete' : 'Pending')
                        : (doc.status === 'draft' ? 'Draft' : 'Filed'),
                      className: doc.requires_signatures
                        ? (doc.all_signatures_collected
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800')
                        : (doc.status === 'draft'
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-blue-100 text-blue-800'),
                    }}
                    subtitle={`${doc.document_category} • ${doc.effective_date ? format(new Date(doc.effective_date), 'MMM d, yyyy') : 'No effective date'}`}
                    onClick={() => navigate(`/governance-documents/${doc.id}`)}
                    actions={
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(doc);
                          }}
                          disabled={isGeneratingPdf === doc.id || (!doc.signed_file_url && !doc.content_html)}
                        >
                          {isGeneratingPdf === doc.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </Button>
                        {canDelete && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive hover:text-destructive"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Governance Document?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to permanently delete "{doc.document_title}"? 
                                  This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(doc.id)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  {deletingDocId === doc.id ? "Deleting..." : "Delete"}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    }
                    expandedContent={
                      <div className="space-y-2 text-sm">
                        <p>
                          <span className="text-muted-foreground">Category:</span>{' '}
                          <Badge variant="secondary" className={getCategoryBadge(doc.document_category)}>
                            {doc.document_category}
                          </Badge>
                        </p>
                        <p>
                          <span className="text-muted-foreground">Type:</span>{' '}
                          {doc.document_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                        {doc.effective_date && (
                          <p>
                            <span className="text-muted-foreground">Effective:</span>{' '}
                            {format(new Date(doc.effective_date), 'MMMM d, yyyy')}
                          </p>
                        )}
                        <p>
                          <span className="text-muted-foreground">Status:</span>{' '}
                          {doc.requires_signatures ? (
                            doc.all_signatures_collected ? (
                              <span className="text-green-600 font-medium">Fully Signed</span>
                            ) : (
                              <span className="text-yellow-600 font-medium">Pending Signatures</span>
                            )
                          ) : (
                            <span className="text-blue-600 font-medium">
                              {doc.status ? doc.status.charAt(0).toUpperCase() + doc.status.slice(1) : 'Filed'}
                            </span>
                          )}
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/governance-documents/${doc.id}`);
                          }}
                        >
                          View Details
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    }
                  />
                ))}
                
                {/* Pagination */}
                {hasMoreDocuments && !showAllDocuments && (
                  <Button
                    variant="ghost"
                    className="w-full text-sm text-muted-foreground hover:text-foreground mt-4"
                    onClick={() => setShowAllDocuments(true)}
                  >
                    Show All Documents ({(filteredDocuments?.length || 0) - 3} more)
                  </Button>
                )}
                {showAllDocuments && hasMoreDocuments && (
                  <Button
                    variant="ghost"
                    className="w-full text-sm text-muted-foreground hover:text-foreground mt-4"
                    onClick={() => setShowAllDocuments(false)}
                  >
                    Show Less
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Governance Documents Found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm || categoryFilter !== "all" || typeFilter !== "all"
                    ? "Try adjusting your filters"
                    : "Governance documents will appear here once they are created and signed"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
